
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineNotificationsActive } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import BottomNavigation from '../../components/feature/BottomNavigation';
import FactModal from '../../components/feature/FactModal';
import { FactNotificationSystem } from '../../utils/factNotificationSystem';
import { type HomeopathyFact } from '../../services/factService';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import FamilyService, { supabaseRestRequest } from '../../services/familyService';
import PointsService, { type FamilyPoints, type LevelTitle } from '../../services/pointsService';
import NotificationService, { type Notification } from '../../services/notificationService';
import DailyStatsService, { type DailyStats } from '../../services/dailyStatsService';
import { getTodayBRT } from '../../utils/dateUtils';
import { isMedicationEffectiveToday } from '../../utils/pauseUtils';
import StatCard from '../../components/feature/StatCard';
import AdherenceBar from '../../components/feature/AdherenceBar';
import { logger } from '../../utils/logger';

interface Member {
  id: string;
  name: string;
  avatar: string;
  streak: number;
  points: number;
  status: 'excellent' | 'good' | 'warning';
}

const isImageAvatar = (avatar?: string) =>
  typeof avatar === 'string' &&
  (avatar.startsWith('/Imagens/') || avatar.startsWith('http') || avatar.startsWith('data:'));


export default function Home() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { token: pushToken } = usePushNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [familyName, setFamilyName] = useState('Família');
  const [familyPoints, setFamilyPoints] = useState<FamilyPoints | null>(null);
  const [familyLevelTitle, setFamilyLevelTitle] = useState<LevelTitle | null>(null);
  const familyService = FamilyService.getInstance();
  const pointsService = PointsService.getInstance();
  const notificationService = NotificationService.getInstance();
  const dailyStatsService = DailyStatsService.getInstance();

  // notifications state and generation logic
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [showPushCta, setShowPushCta] = useState(false);

  const handleGoToPushSettings = useCallback(() => {
    setShowNotifications(false);
    navigate('/settings?focus=push');
  }, [navigate]);

  useEffect(() => {
    if (pushToken) {
      try { localStorage.removeItem('push_cta:just_ended_at') } catch { /* ignore */ }
      setShowPushCta(false);
      return;
    }

    if (!showNotifications) {
      setShowPushCta(false);
      return;
    }

    setShowPushCta(true);
  }, [pushToken, showNotifications]);

  // Avatares: fallback e normalização de extensão
  const [brokenAvatars, setBrokenAvatars] = useState<Record<string, boolean>>({});
  const getAvatarSrc = useCallback((avatar?: string) => {
    if (!avatar || typeof avatar !== 'string') return '';
    // Qualquer caminho que contenha /Imagens/ (mesmo URL absoluto) → mapear para asset local .webp
    if (avatar.includes('/Imagens/')) {
      try {
        const after = avatar.split('/Imagens/')[1] || '';
        const fileWithQuery = after.split('?')[0];
        const decoded = decodeURIComponent(fileWithQuery);
        const normalized = decoded.replace(/\.(png|jpg|jpeg|webp)$/i, '.webp');
        return `/Imagens/${normalized}`;
      } catch {
        // fallback: tentar apenas troca de extensão se começar local
        if (avatar.startsWith('/Imagens/')) {
          return avatar.replace(/\.(png|jpg|jpeg|webp)(\?.*)?$/i, '.webp$2');
        }
      }
    }
    return avatar;
  }, []);

  // Ocultação manual de lembretes (persistência local)
  const getReminderRev = useCallback((n: Notification) => {
    const t = (n.scheduledTime || '').substring(0, 5);
    return `${n.title}|${n.description}|${t}`;
  }, []);
  const getDismissKey = useCallback((n: Notification) => {
    return `dismiss:reminder:${user?.id || 'anon'}:${n.reminderId}:${getReminderRev(n)}`;
  }, [getReminderRev, user?.id]);
  const isReminderDismissed = useCallback((n: Notification) => {
    if (n.type !== 'reminder' || !n.reminderId) return false;
    return localStorage.getItem(getDismissKey(n)) === '1';
  }, [getDismissKey]);
  const dismissReminder = useCallback((n: Notification) => {
    if (n.type !== 'reminder' || !n.reminderId) return;
    try {
      localStorage.setItem(getDismissKey(n), '1');
      setNotifications((prev) => prev.filter((notification) => notification.reminderId !== n.reminderId));
    } catch {
      /* ignore localStorage errors */
    }
  }, [getDismissKey]);
  const visibleNotifications = useMemo(() => {
    return notifications.filter((notification) => notification.type !== 'reminder' || !isReminderDismissed(notification));
  }, [notifications, isReminderDismissed]);

  const alertsBadgeCount = useMemo(() => {
    return visibleNotifications.length + (pushToken ? 0 : 1);
  }, [visibleNotifications.length, pushToken]);

  // Somente doses atrasadas (como exibidas no modal Alertas do Dia)
  const overdueDosesCount = useMemo(() => {
    return visibleNotifications.filter((n) => n.type === 'medication' && n.title === 'Dose atrasada').length;
  }, [visibleNotifications]);

  // Sistema de curiosidades
  const [factNotificationSystem] = useState(() => FactNotificationSystem.getInstance());
  const [showFactModal, setShowFactModal] = useState(false);
  const [currentFact, setCurrentFact] = useState<HomeopathyFact | null>(null);
  const [factNotificationStatus, setFactNotificationStatus] = useState({
    hasUnreadFacts: false,
    canShowNow: false,
    currentFactHasEarnedPoints: false
  });
  const [hasEarnedPointsFor24h, setHasEarnedPointsFor24h] = useState(false);
  const [readFactsHistory, setReadFactsHistory] = useState<string[]>([]);

  // Estatísticas diárias
  const [dailyStats, setDailyStats] = useState<DailyStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [adherenceOverride, setAdherenceOverride] = useState<Record<string, { taken: number; total: number; percentage: number; status: 'excellent' | 'good' | 'warning' | 'none' }>>({});

  // Totais diários (tomadas/total/%) ignorando pausas — somatório dos overrides por membro
  const medicationTotalsOverride = useMemo(() => {
    const ids = Object.keys(adherenceOverride);
    if (ids.length === 0) return null;
    let taken = 0;
    let total = 0;
    for (const id of ids) {
      const o = adherenceOverride[id];
      taken += o.taken || 0;
      total += o.total || 0;
    }
    const percentage = total > 0 ? Math.round((taken / total) * 100) : 100;
    return { taken, total, percentage };
  }, [adherenceOverride]);

  // Carregar notificações reais do banco de dados
  const loadNotifications = useCallback(async () => {
    if (!familyId) return;
    
    logger.info('🔔 [Home] Carregando notificações da família...');
    setLoadingNotifications(true);
    
    try {
      const familyNotifications = await notificationService.getFamilyDailyNotifications(familyId);
      setNotifications(familyNotifications);
      logger.success('[Home] Notificações carregadas:', familyNotifications.length);
    } catch (error) {
      console.error('❌ [Home] Erro ao carregar notificações:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [familyId, notificationService]);

  // Verificar se é um novo dia e recarregar notificações
  const checkAndReloadNotifications = useCallback(() => {
    if (notificationService.isNewDay()) {
      logger.info('🌅 [Home] Novo dia detectado! Recarregando notificações...');
      void loadNotifications();
    }
  }, [notificationService, loadNotifications]);

  // Carregar estatísticas diárias
  const loadDailyStats = useCallback(async () => {
    if (!familyId) return;
    
    logger.info('📊 [Home] Carregando estatísticas diárias...');
    setLoadingStats(true);
    
    try {
      // Verificar se há membros com medicações cadastradas
      const members = await familyService.getFamilyMembers(familyId);
      let hasMedications = false;
      
      for (const member of members) {
        if (member.id) {
          const medications = await familyService.getMemberMedications(member.id);
          if (medications && medications.length > 0) {
            hasMedications = true;
            break;
          }
        }
      }
      
      // Se não há medicações, não carregar estatísticas (mostrar mensagem de boas-vindas)
      if (!hasMedications) {
        logger.info('ℹ️ [Home] Nenhuma medicação cadastrada, pulando estatísticas');
        setDailyStats(null);
        setLoadingStats(false);
        return;
      }
      
      // Adicionar timeout de 10 segundos para evitar loading infinito
      const statsPromise = dailyStatsService.getFamilyDailyStats(familyId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout ao carregar estatísticas')), 20000)
      );
      
      const stats = await Promise.race([statsPromise, timeoutPromise]) as any;
      setDailyStats(stats);
      logger.debug('[Home] Estatísticas carregadas:', stats);
    } catch (error) {
      console.error('❌ [Home] Erro ao carregar estatísticas:', error);
      setDailyStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [familyId, familyService, dailyStatsService]);

  // Helpers locais (removidos: parseLocalDate e wrapper de pausa; usamos pauseUtils diretamente)
  // Normalizador de times (pode vir string JSON do banco)
  const getTimesArray = (times: any): string[] => {
    // 1) Já é array
    if (Array.isArray(times)) {
      return (times as any[])
        .map((t) => (typeof t === 'string' ? t : String(t)))
        .map((t) => (t.length >= 5 ? t.substring(0, 5) : t))
        .filter((t) => /^\d{2}:\d{2}$/.test(t));
    }

    // 2) String
    if (typeof times === 'string') {
      const s = times.trim();
      // 2.1) JSON '["09:00","21:00"]'
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed
            .map((t: any) => (typeof t === 'string' ? t : String(t)))
            .map((t: string) => (t.length >= 5 ? t.substring(0, 5) : t))
            .filter((t: string) => /^\d{2}:\d{2}$/.test(t));
        }
      } catch {
        // segue para outros formatos
      }

      // 2.2) Postgres text[] '{09:00,21:00}'
      if (s.startsWith('{') && s.endsWith('}')) {
        const body = s.slice(1, -1);
        return body
          .split(',')
          .map((p) => p.trim().replace(/^"|"$/g, ''))
          .map((t) => (t.length >= 5 ? t.substring(0, 5) : t))
          .filter((t) => /^\d{2}:\d{2}$/.test(t));
      }

      // 2.3) Extrair HH:mm (ou HH:mm:ss)
      const matches = s.match(/\b([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?\b/g);
      if (matches) {
        return matches
          .map((t) => (t.length >= 5 ? t.substring(0, 5) : t))
          .filter((t) => /^\d{2}:\d{2}$/.test(t));
      }

      return [];
    }

    return [];
  };

  // Recalcular aderência diária por membro ignorando medicações em pausa
  useEffect(() => {
    const compute = async () => {
      if (!dailyStats || !members || members.length === 0) return;
      try {
        const today = getTodayBRT();
        const map: Record<string, { taken: number; total: number; percentage: number; status: 'excellent' | 'good' | 'warning' | 'none' }> = {};
        await Promise.all(members.map(async (m) => {
          try {
            const meds = await familyService.getMemberMedications(m.id);
            // Usar helper unificado (exclui futuras, finalizadas e pausadas)
            const effectiveMeds = meds.filter((med: any) => isMedicationEffectiveToday(med, today));
            const total = effectiveMeds.reduce((sum: number, med: any) => sum + getTimesArray(med.times).length, 0);
            const memberMedData = dailyStats.medication.byMember.find((x) => x.memberId === m.id);
            const taken = memberMedData?.taken || 0;
            const percentage = total > 0 ? Math.round((taken / total) * 100) : 100;
            const status: 'excellent' | 'good' | 'warning' | 'none' = total === 0 ? 'excellent' : (percentage >= 90 ? 'excellent' : percentage >= 70 ? 'good' : 'warning');
            map[m.id] = { taken, total, percentage, status };
          } catch (err) {
            console.warn(`[Home] Falha ao calcular aderência para membro ${m.id}:`, err);
            // fallback: manter 0/0 para não distorcer o total
            map[m.id] = { taken: 0, total: 0, percentage: 100, status: 'excellent' };
          }
        }));
        setAdherenceOverride(map);
      } catch (e) {
        console.warn('⚠️ [Home] Falha ao recalcular aderência diária ignorando pausas:', e);
        setAdherenceOverride({});
      }
    };
    void compute();
  }, [dailyStats, members, familyService]);

  // Carregar notificações e estatísticas quando familyId estiver disponível
  useEffect(() => {
    if (familyId) {
      void loadNotifications();
      void loadDailyStats();
      
      // Atualizar a cada 1 minuto
      const interval = setInterval(() => {
        checkAndReloadNotifications();
        void loadNotifications();
        void loadDailyStats();
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [familyId, loadNotifications, loadDailyStats, checkAndReloadNotifications]);

  const refreshFactStatus = useCallback(async () => {
    if (!user?.id) return null;
    const status = await factNotificationSystem.getFactNotificationStatus(user.id);
    setFactNotificationStatus({
      hasUnreadFacts: status.hasUnreadFacts,
      canShowNow: status.canShowNow,
      currentFactHasEarnedPoints: status.currentFactHasEarnedPoints,
    });
    setHasEarnedPointsFor24h(status.currentFactHasEarnedPoints);

    if (status.currentFactId && !status.canShowNow) {
      const fact = await factNotificationSystem.getLastFactShown(user.id);
      if (fact) {
        setCurrentFact(fact);
      }
    }

    const history = await factNotificationSystem.getReadFactsHistory(user.id);
    setReadFactsHistory(history);
    return status;
  }, [factNotificationSystem, user?.id]);

  useEffect(() => {
    const checkForFacts = async () => {
      try {
        if (!user?.id) return;
        const result = await factNotificationSystem.checkForNotification(user.id);
        const status = await refreshFactStatus();

        if (result.shouldShow && result.fact) {
          setCurrentFact(result.fact);
          setHasEarnedPointsFor24h(false);
        }

        if (status && !status.canShowNow) {
          setShowFactModal(false);
        }
      } catch (error) {
        console.error('Erro ao verificar curiosidades:', error);
      }
    };

    checkForFacts();
    const factInterval = setInterval(checkForFacts, 30 * 60 * 1000);
    return () => clearInterval(factInterval);
  }, [factNotificationSystem, refreshFactStatus, user?.id]);

  const handleFactModalClose = () => {
    setShowFactModal(false);
  };

  const handleFactRead = async (factId: string) => {
    if (!user?.id) return;
    await factNotificationSystem.markFactAsRead(factId, user.id);
    setShowFactModal(false);
    setHasEarnedPointsFor24h(true);
  };

  const handleFactReadWithPoints = async (factId: string): Promise<boolean> => {
    if (!user) return false;

    const earnedPoints = await factNotificationSystem.markFactAsReadWithPoints(factId, user.id);
    if (earnedPoints) {
      console.log('✅ Pontos ganhos para curiosidade:', factId);

      
      // Recarregar pontos da família (refetch direto + pequeno retry)
      try {
        const family = await familyService.getUserFamily();
        if (family && family.id) {
          const prevTotal = familyPoints?.totalPoints ?? 0;
          let tries = 0;
          let refreshed: any = null;
          while (tries < 3) {
            const data = await supabaseRestRequest(
              `family_points?family_id=eq.${family.id}&select=*`,
              'GET'
            );
            refreshed = Array.isArray(data) ? data[0] : data;
            if (refreshed && (refreshed.total_points ?? 0) !== prevTotal) break;
            await new Promise(r => setTimeout(r, 300));
            tries++;
          }

          if (refreshed) {
            const fp = {
              id: refreshed.id,
              familyId: refreshed.family_id,
              totalPoints: refreshed.total_points ?? 0,
              totalCuriositiesRead: refreshed.total_curiosities_read ?? 0,
              totalStreaksAchieved: refreshed.total_streaks_achieved ?? 0,
              level: refreshed.level ?? 1,
              createdAt: refreshed.created_at,
              updatedAt: refreshed.updated_at,
            } as FamilyPoints;

            setFamilyPoints(fp);

            // Atualizar título do nível
            const title = await pointsService.getLevelTitle(fp.totalPoints, 'family');
            if (title) {
              setFamilyLevelTitle(title);
            } else {
              const fallbackTitle = await pointsService.getLevelTitleByLevel(fp.level ?? 1, 'family');
              setFamilyLevelTitle(fallbackTitle);
            }

            // Disparar evento global
            try {
              window.dispatchEvent(new CustomEvent('magistral:family-points-updated', { detail: { familyId: fp.familyId } }));
            } catch {
              /* ignore dispatch errors (e.g., window not available) */
            }

            console.log('✅ Pontos da família atualizados:', fp);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao recarregar pontos da família:', error);
      }
    }
    
    await refreshFactStatus();
    setShowFactModal(false);
    setHasEarnedPointsFor24h(true);
    return earnedPoints;
  };

  const handleLogoClick = async () => {
    // Obter a última curiosidade mostrada
    if (!user?.id) return;
    const factStatus = await refreshFactStatus();

    if (factStatus && !factStatus.canShowNow && factStatus.currentFactId) {
      if (!currentFact || currentFact.id !== factStatus.currentFactId) {
        const fact = await factNotificationSystem.getLastFactShown(user.id);
        if (fact) {
          setCurrentFact(fact);
        }
      }
      setShowFactModal(true);
      return;
    }

    if (currentFact && (factStatus?.canShowNow ?? false)) {
      setShowFactModal(true);
      return;
    }

    const result = await factNotificationSystem.forceNextNotification(user.id);
    if (result.fact) {
      setCurrentFact(result.fact);
      setHasEarnedPointsFor24h(false);
      setShowFactModal(true);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 'medication' && notification.memberId) {
      navigate(`/member/${notification.memberId}`);
    }
    setShowNotifications(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500';
      case 'good': return 'bg-blue-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-50 border-emerald-200';
      case 'good': return 'bg-blue-50 border-blue-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const handleMemberClick = (memberId: string) => {
    navigate(`/member/${memberId}`);
  };

  // Carregar família e membros usando função otimizada do banco
  useEffect(() => {
    const loadFamilyData = async () => {
      logger.info('⚡ [Home] Carregando dashboard com função otimizada...');
      
      try {
        // Buscar o token de autenticação diretamente do localStorage
        const tokensStr = localStorage.getItem('supabase.auth.token');
        if (!tokensStr) {
          throw new Error('Token de autenticação não encontrado no localStorage.');
        }
        
        const tokens = JSON.parse(tokensStr);
        const accessToken = tokens.access_token;
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const userId = payload.sub;
        
        logger.sensitive('User ID', { userId });
        
        // Chamar função otimizada que retorna TUDO de uma vez
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/get_dashboard_data`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ p_user_id: userId })
          }
        );
        
        if (!response.ok) {
          throw new Error(`Erro na requisição: ${response.status}`);
        }
        
        const dashboardData = await response.json();
        logger.debug('[Home] Dashboard data carregado:', dashboardData);
        
        // Extrair dados
        const { family, members: membersData, family_points } = dashboardData;
        
        if (family) {
          logger.info('✅ [Home] Família:', family.name);
          setFamilyName(family.name);
          setFamilyId(family.id);
          
          // Converter family_points de snake_case para camelCase
          if (family_points) {
            const familyPointsData = {
              id: family_points.id,
              familyId: family_points.family_id,
              totalPoints: family_points.total_points,
              totalCuriositiesRead: family_points.total_curiosities_read,
              totalStreaksAchieved: family_points.total_streaks_achieved,
              level: family_points.level,
              createdAt: family_points.created_at,
              updatedAt: family_points.updated_at
            };
            setFamilyPoints(familyPointsData);
            logger.info('✅ [Home] Pontos da família:', family_points.total_points);
            
            // Buscar título do nível da família
            pointsService.getLevelTitle(familyPointsData.totalPoints, 'family')
              .then(async (title) => {
                if (title) {
                  setFamilyLevelTitle(title);
                } else {
                  const fallbackTitle = await pointsService.getLevelTitleByLevel(familyPointsData.level ?? 1, 'family');
                  setFamilyLevelTitle(fallbackTitle);
                }
                logger.info('🏅 [Home] Título da família:', title?.title);
              })
              .catch(err => console.warn('� ️ [Home] Erro ao buscar título:', err));
          }
          
          // DESABILITADO TEMPORARIAMENTE: Verificação de streaks agora é feita APENAS pelos jobs diários (00:01)
          // TODO: Reativar após corrigir descrições genéricas no histórico de pontos
          // Ref: Teste 4 - Problema A e B identificados em 22/10/2025
          // if (family.id) {
          //   pointsService.checkFamilyDailyStreaks(family.id)
          //     .then(() => console.log('✅ [Home] Streaks verificados'))
          //     .catch(err => console.warn('� ️ [Home] Erro ao verificar streaks:', err));
          // }
        }
        
        // Converter membros para formato do dashboard
        const formattedMembers: Member[] = membersData.map((member: any) => {
          const points = member.points || {};
          const totalPoints = points.total_points || 0;
          const currentStreak = points.current_treatment_streak || 0;
          
          // Calcular status baseado nos pontos
          let status: 'excellent' | 'good' | 'warning' = 'warning';
          if (totalPoints >= 1000) {
            status = 'excellent';
          } else if (totalPoints >= 300) {
            status = 'good';
          }
          
          return {
            id: member.id,
            name: member.name,
            avatar: member.avatar_url || 'ri-user-line',
            streak: currentStreak,
            points: totalPoints,
            status
          };
        });
        
        setMembers(formattedMembers);
        logger.success('[Home] Dashboard carregado com sucesso!');
      } catch (error) {
        console.error('❌ [Home] Erro ao carregar dashboard:', error);
        setMembers([]);
      }
    };

    loadFamilyData();
  }, [pointsService]);

  // Efeito para controlar a animação de entrada
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100); // Pequeno delay para garantir que o componente foi montado

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-t from-orange-200 to-white pb-20">
      {/* Header */}
      <div 
        className={`px-6 py-10 pt-12 pb-8 rounded-b-3xl shadow-card relative bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-out transform ${
          isLoaded 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 -translate-y-8'
        }`}
        style={{
          backgroundImage: "url('/Imagens/familia dashboard.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay para melhorar legibilidade do texto */}
        <div className="absolute inset-0 bg-black/20 rounded-b-3xl"></div>
        
        {/* Conteúdo do header */}
        <div className="relative z-10">
          <div className={`flex items-center justify-between mb-36 transition-all duration-1200 ease-out delay-300 ${
            isLoaded 
              ? 'opacity-100 translate-x-0' 
              : 'opacity-0 -translate-x-4'
          }`}>
            <div className="flex items-center">
              <div className="relative w-10 h-10 rounded-lg flex items-center justify-center mr-3 bg-white/20 cursor-pointer hover:bg-white/30 transition-colors" onClick={handleLogoClick} data-tutorial-anchor="btn-open-curiosities">
                <img 
                  src="/Imagens/logo magistral 2.0 verde.png"
                  alt="Magistral Logo"
                  className="w-full h-full object-cover object-center rounded-lg"
                  loading="lazy"
                />
                
                {/* Indicador de curiosidade não lida - lâmpada piscando */}
                {factNotificationStatus.hasUnreadFacts && !factNotificationStatus.currentFactHasEarnedPoints && factNotificationStatus.canShowNow && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center animate-pulse z-20 shadow-lg border-2 border-white">
                    <i className="ri-lightbulb-line text-white text-sm font-bold"></i>
                  </div>
                )}
                
                {/* Indicador de curiosidade lida - check verde (quando há curiosidades não lidas mas a atual já foi lida) */}
                {factNotificationStatus.hasUnreadFacts && (factNotificationStatus.currentFactHasEarnedPoints || !factNotificationStatus.canShowNow) && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center z-20 shadow-lg border-2 border-white">
                    <i className="ri-check-line text-white text-sm font-bold"></i>
                  </div>
                )}
                
                {/* Indicador de curiosidade lida quando não há mais curiosidades não lidas */}
                {!factNotificationStatus.hasUnreadFacts && readFactsHistory.length > 0 && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center z-20 shadow-lg border-2 border-white">
                    <i className="ri-check-line text-white text-sm font-bold"></i>
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white font-sans" style={{ 
                  fontWeight: 700,
                  textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
                }}>
                  Magistral
                </h1>
                <p className="text-white text-sm font-sans" style={{ fontWeight: 500, color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>{familyName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center relative hover:bg-white/30 transition-colors" data-tutorial-anchor="btn-bell-alerts"
              >
                <i className="ri-notification-2-line text-white text-xl"></i>
                {alertsBadgeCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold" style={{ color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                      {alertsBadgeCount > 9 ? '9+' : alertsBadgeCount}
                    </span>
                  </div>
                )}
              </button>
              
              <button 
                onClick={() => {
                  try {
                    void signOut();
                    navigate('/login');
                  } catch (error) {
                    console.error('Erro ao fazer logout:', error);
                  }
                }}
                className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-red-500/50 transition-colors relative z-[10002]"
                title="Sair"
              >
                <i className="ri-logout-box-r-line text-white text-xl"></i>
              </button>
            </div>
          </div>

          {/* Pontos da Família */}
          <div className={`bg-white/20 backdrop-blur-sm rounded-card p-4 transition-all duration-1000 ease-out delay-500 ${
            isLoaded 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-4 scale-95'
          }`} data-tutorial-anchor="dashboard-summary">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-white text-sm mb-1 font-sans" style={{ fontWeight: 500, color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>Pontos da Família</p>
                <div className="flex items-center gap-3">
                  <p className="text-3xl font-bold text-white font-sans" style={{ fontWeight: 700, color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                    {familyPoints?.totalPoints || 0}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-trophy-fill text-yellow-300 text-xl"></i>
                      <span className="text-white text-sm font-sans" style={{ fontWeight: 600, color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                        Nível {familyPoints?.level || 1}
                      </span>
                    </div>
                    {familyLevelTitle && (
                      <span className="text-white/90 text-xs font-sans ml-6" style={{ fontWeight: 500, color: '#FFFFFF', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                        {familyLevelTitle.title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Painel de Notificações - Aprimorado */}
      {showNotifications && (
        <div className="fixed top-20 right-6 bg-white rounded-card shadow-card w-96 max-h-[32rem] overflow-hidden z-[99999]" data-tutorial-anchor="alerts-panel">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 font-sans" style={{ fontWeight: 700 }}>Alertas do Dia</h3>
                {visibleNotifications.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {visibleNotifications.filter(n => n.priority === 'high').length} urgentes
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowNotifications(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-gray-600 text-xl"></i>
              </button>
            </div>
          </div>

          {showPushCta && (
            <div className="px-4 py-3 border-b border-gray-100">
              <button
                type="button"
                onClick={handleGoToPushSettings}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Ative os alertas para receber as notificações no celular
              </button>
            </div>
          )}

          <div className="max-h-[24rem] overflow-y-auto">
            {loadingNotifications ? (
              <div className="p-6 text-center">
                <i className="ri-loader-4-line text-3xl text-gray-400 mb-2 animate-spin"></i>
                <p className="text-gray-500 text-sm">Carregando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <i className="ri-notification-off-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-gray-500 text-sm">Nenhum alerta para hoje</p>
                <p className="text-gray-400 text-xs mt-1">Você está em dia! 🎉</p>
              </div>
            ) : (
              <>
                {/* Tratamentos em Pausa */}
                {notifications.filter(n => n.title === 'Tratamento em pausa').length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
                      <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        ⏸️ Tratamentos em pausa
                      </h4>
                    </div>
                    {notifications
                      .filter(n => n.title === 'Tratamento em pausa')
                      .map((notification) => (
                        <div 
                          key={notification.id}
                          onClick={() => setShowNotifications(false)}
                          className="p-4 border-b border-gray-100 hover:bg-amber-50/60 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            {!brokenAvatars[notification.memberId] && notification.memberAvatar ? (
                              <img 
                                src={getAvatarSrc(notification.memberAvatar)} 
                                alt={notification.memberName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={() => setBrokenAvatars(prev => ({ ...prev, [notification.memberId]: true }))}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-pause-circle-line text-amber-600 text-lg"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <p className="text-sm font-semibold text-amber-700">
                                  {notification.title}
                                </p>
                                <span className="text-xs text-amber-600 ml-2 flex-shrink-0 font-medium">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="text-xs text-gray-700 font-medium mb-0.5">
                                {notification.memberName}
                              </p>
                              <p className="text-xs text-gray-600">
                                {notification.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* Doses Atrasadas */}
                {notifications.filter(n => n.title.includes('atrasada')).length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                      <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                        🚨 Doses Atrasadas
                      </h4>
                    </div>
                    {notifications
                      .filter(n => n.title.includes('atrasada'))
                      .map((notification) => (
                        <div 
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="p-4 border-b border-gray-100 hover:bg-red-50/50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            {!brokenAvatars[notification.memberId] && notification.memberAvatar ? (
                              <img 
                                src={getAvatarSrc(notification.memberAvatar)} 
                                alt={notification.memberName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={() => setBrokenAvatars(prev => ({ ...prev, [notification.memberId]: true }))}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-red-600 text-lg"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <p className="text-sm font-semibold text-red-700">
                                  {notification.title}
                                </p>
                                <span className="text-xs text-red-600 ml-2 flex-shrink-0 font-medium">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="text-xs text-gray-700 font-medium mb-0.5">
                                {notification.memberName}
                              </p>
                              <p className="text-xs text-gray-600">
                                {notification.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* Próximas Doses */}
                {notifications.filter(n => n.title.includes('Próxima') || n.title.includes('Hora da')).length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-orange-50 border-b border-orange-100">
                      <h4 className="text-xs font-semibold text-orange-700 uppercase tracking-wide">
                        ⏰ Próximas Doses
                      </h4>
                    </div>
                    {notifications
                      .filter(n => n.title.includes('Próxima') || n.title.includes('Hora da'))
                      .map((notification) => (
                        <div 
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="p-4 border-b border-gray-100 hover:bg-orange-50/50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            {!brokenAvatars[notification.memberId] && notification.memberAvatar ? (
                              <img 
                                src={getAvatarSrc(notification.memberAvatar)} 
                                alt={notification.memberName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={() => setBrokenAvatars(prev => ({ ...prev, [notification.memberId]: true }))}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-orange-600 text-lg"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <p className="text-sm font-semibold text-orange-700">
                                  {notification.title}
                                </p>
                                <span className="text-xs text-orange-600 ml-2 flex-shrink-0 font-medium">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="text-xs text-gray-700 font-medium mb-0.5">
                                {notification.memberName}
                              </p>
                              <p className="text-xs text-gray-600">
                                {notification.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* Lembretes */}
                {visibleNotifications.filter(n => n.type === 'reminder').length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                      <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        📌 Lembretes
                      </h4>
                    </div>
                    {visibleNotifications
                      .filter(n => n.type === 'reminder')
                      .map((notification) => (
                        <div 
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="p-4 border-b border-gray-100 hover:bg-blue-50/50 cursor-pointer transition-colors"
                        >
                          {/* Linha superior apenas com o botão de fechar alinhado �  direita (fora do bloco avatar+conteúdo) */}
                              <div className="flex justify-end h-[1.25rem] mb-0.5">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); dismissReminder(notification); }}
                                  className="flex items-center justify-center w-[1.25rem] h-[1.25rem] hover:bg-gray-100 active:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
                                  title="Ocultar este lembrete"
                                  aria-label="Fechar alerta"
                                >
                                  <i className="ri-close-line text-red-600 text-sm" aria-hidden="true"></i>
                                </button>
                              </div>

                          <div className="flex items-start space-x-3">
                            {!brokenAvatars[notification.memberId] && notification.memberAvatar ? (
                              <img 
                                src={getAvatarSrc(notification.memberAvatar)} 
                                alt={notification.memberName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={() => setBrokenAvatars(prev => ({ ...prev, [notification.memberId]: true }))}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-blue-600 text-lg"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Linha com título e horário */}
                              <div className="flex items-start justify-between mb-0">
                                <div className="flex flex-col">
                                  <p className="text-sm font-semibold text-blue-700 leading-tight">
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-700 font-medium leading-snug">
                                    {notification.memberName}
                                  </p>
                                </div>
                                <span className="text-xs text-blue-600 ml-2 flex-shrink-0 font-medium">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {notification.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* Doses Futuras */}
                {notifications.filter(n => n.title.includes('agendada')).length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        📅 Doses Agendadas
                      </h4>
                    </div>
                    {notifications
                      .filter(n => n.title.includes('agendada'))
                      .map((notification) => (
                        <div 
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            {!brokenAvatars[notification.memberId] && notification.memberAvatar ? (
                              <img 
                                src={getAvatarSrc(notification.memberAvatar)} 
                                alt={notification.memberName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={() => setBrokenAvatars(prev => ({ ...prev, [notification.memberId]: true }))}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-gray-600 text-lg"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <p className="text-sm font-medium text-gray-700">
                                  {notification.memberName}
                                </p>
                                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {notification.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Overlay para fechar notificações */}
      {showNotifications && (
        <div className="fixed inset-0 z-[99998]" onClick={() => setShowNotifications(false)}></div>
      )}

      {/* Membros da Família */}
      <div className="px-6 py-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 font-sans" style={{ fontWeight: 700 }}>Membros da Família</h2>
        
        <div className="grid grid-cols-2 gap-4">
          {members.map((member) => (
            <div 
              key={member.id}
              onClick={() => handleMemberClick(member.id)}
              className={`p-4 rounded-card border transition-all hover:scale-105 cursor-pointer shadow-soft ${getStatusBg(member.status)} relative`}
            >
              {/* Indicador de notificações pendentes para o membro */}
              {visibleNotifications.filter(n => n.memberId === member.id && n.priority === 'high').length > 0 && (
                <div className="absolute -top-3 -right-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgba(239,68,68,0.35)] border border-red-100">
                    <MdOutlineNotificationsActive className="h-5 w-5 text-red-500 animate-wiggle" />
                  </div>
                </div>
              )}
              
              <div className="text-center">
                {/* Avatar */}
                <div className={`w-16 h-16 ${getStatusColor(member.status)} rounded-card flex items-center justify-center mb-3 mx-auto shadow-soft overflow-hidden`}>
                  {!brokenAvatars[member.id] && isImageAvatar(member.avatar) ? (
                    <img
                      src={getAvatarSrc(member.avatar)}
                      alt={`Avatar de ${member.name}`}
                      className="w-full h-full object-cover"
                      onError={() => setBrokenAvatars(prev => ({ ...prev, [member.id]: true }))}
                    />
                  ) : (
                    <i className={`${member.avatar && member.avatar.startsWith('ri-') ? member.avatar : 'ri-user-line'} text-2xl text-white`}></i>
                  )}
                </div>

                <h3 className="font-semibold text-gray-800 mb-2 font-sans" style={{ fontWeight: 700 }}>{member.name}</h3>
                
                {/* Pontos */}
                <div className="mb-2">
                  <p className="text-2xl font-bold text-gray-800 font-sans" style={{ fontWeight: 700 }}>{member.points}</p>
                  <p className="text-xs text-gray-600 font-sans" style={{ fontWeight: 500 }}>pontos</p>
                </div>
                
                {/* Streak */}
                <div className="flex items-center justify-center">
                  <i className="ri-fire-fill text-orange-500 text-sm mr-1"></i>
                  <span className="text-sm font-medium text-gray-700 font-sans" style={{ fontWeight: 500 }}>{member.streak} dias</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resumo de Hoje - Expandido */}
      <div className="px-6">
        <div className="bg-white rounded-card shadow-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800 font-sans" style={{ fontWeight: 700 }}>
              📊 Resumo de Hoje
            </h3>
          </div>
          
          {loadingStats ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Carregando...</p>
            </div>
          ) : dailyStats ? (
            <>
              {/* Grid de Cards - Linha 1 */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <StatCard
                  icon="ri-medicine-bottle-line"
                  value={`${(medicationTotalsOverride?.taken ?? dailyStats.medication.taken)}/${(medicationTotalsOverride?.total ?? dailyStats.medication.total)}`}
                  subtitle={`${(medicationTotalsOverride?.percentage ?? dailyStats.medication.percentage)}%`}
                  color="emerald"
                />
                
                <StatCard
                  icon="ri-notification-3-line"
                  value={overdueDosesCount}
                  subtitle="Pendentes"
                  color="blue"
                />
                
                <StatCard
                  icon="ri-trophy-line"
                  value={`+${dailyStats.points.earnedToday}`}
                  subtitle="Pontos"
                  color="purple"
                />
              </div>
              
              {/* Grid de Cards - Linha 2 */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <StatCard
                  icon="ri-heart-pulse-line"
                  value={dailyStats.health.positiveEvents}
                  subtitle="Positivos"
                  color="emerald"
                />
                
                <StatCard
                  icon="ri-fire-line"
                  value={dailyStats.streaks.activeMembersCount}
                  subtitle="Streaks"
                  color="orange"
                />
                
                <StatCard
                  icon="ri-question-line"
                  value={`${dailyStats.quiz.completedCount}/${dailyStats.quiz.totalMembers}`}
                  subtitle="Quiz"
                  color="yellow"
                />
              </div>
              
              {/* Barra de Aderência */}
              {dailyStats.adherence.byMember.length > 0 && (
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                  <h4 className="text-xl font-semibold text-gray-700 mb-4 font-sans" style={{ fontWeight: 700 }}>
                    📈 Aderência do dia
                  </h4>
                  
                  {dailyStats.adherence.byMember.map(member => {
                    const memberMedData = dailyStats.medication.byMember.find(m => m.memberId === member.memberId);
                    const ov = adherenceOverride[member.memberId];
                    const taken = ov ? ov.taken : (memberMedData?.taken || 0);
                    const total = ov ? ov.total : (memberMedData?.total || 0);
                    const percentage = ov ? ov.percentage : member.percentage;
                    const status = ov ? ov.status : member.status;
                    return (
                      <AdherenceBar
                        key={member.memberId}
                        memberName={member.memberName}
                        percentage={percentage}
                        taken={taken}
                        total={total}
                        status={status}
                      />
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-3xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-medicine-bottle-line text-4xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Comece sua jornada!</h4>
              <p className="text-sm text-gray-600 mb-6 px-4">
                Adicione o tratamento dos membros da família para começar a acompanhar o progresso e ganhar pontos
              </p>
              <button
                onClick={async () => {
                  try {
                    // Navegar para o primeiro membro já carregado
                    if (members && members.length > 0) {
                      navigate(`/member/${members[0].id}`);
                      return;
                    }
                    // Fallback: buscar membros da família e navegar para o primeiro
                    const fm = await familyService.getUserFamilyMembers();
                    if (Array.isArray(fm) && fm.length > 0 && fm[0]?.id) {
                      navigate(`/member/${fm[0].id}`);
                      return;
                    }
                    console.warn('[Home] Nenhum membro encontrado ao tentar adicionar tratamento.');
                  } catch (e) {
                    console.error('[Home] Falha ao navegar para página do membro ao adicionar tratamento:', e);
                  }
                }}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-blue-500 text-white rounded-2xl font-medium hover:shadow-lg transition-all" data-tutorial-anchor="btn-add-treatment"
              >
                <i className="ri-add-line text-xl mr-2"></i>
                Adicionar Tratamento
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Curiosidade */}
      <FactModal
        isOpen={showFactModal}
        fact={currentFact}
        onClose={handleFactModalClose}
        onMarkAsRead={handleFactRead}
        onMarkAsReadWithPoints={handleFactReadWithPoints}
        hasEarnedPointsToday={hasEarnedPointsFor24h}
      />

      <BottomNavigation />
    </div>
  );
}







